import { api } from "@bindery/convex-reader/api";
import { useLogger } from "@bindery/logging/react";
import type { WordListItem } from "@bindery/shared/reader/page-parsing";
import { Skeleton } from "@bindery/ui/components/ui/skeleton";
import type { FunctionReturnType } from "convex/server";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { saveProgressHref } from "@/auth/links";
import { convex } from "@/convex/client";
import {
  readerIdOf,
  type SessionStore,
  sessionStore,
  useSession,
} from "@/convex/session";
import { SightVocabularyExercise } from "@/ear-reader/vocabulary-exercise/sight-vocabulary-exercise";

type LearnedWords = NonNullable<
  FunctionReturnType<typeof api.fluency.learnedWords>
>;

// djb2: the same slug always gets the same id, whatever the list order.
export function hashToId(text: string): number {
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 33) ^ text.charCodeAt(i);
  }
  return Math.abs(hash);
}

export function toWordListItems(words: LearnedWords["words"]): WordListItem[] {
  return words.map((entry) => ({
    id: hashToId(entry.slug),
    text: entry.word,
    slug: entry.slug,
  }));
}

type WordsState =
  | { status: "loading" }
  | { status: "failed" }
  | { status: "ready"; words: WordListItem[]; truncated: boolean };

const PAGE = "container mx-auto px-4 py-12 md:px-6";

const fetchLearnedWords = () => convex.query(api.fluency.learnedWords, {});

// One query, not a subscription: learnedWords scans every fluency row, and
// a subscription would re-run it after every tap (schema cost note).
export function WordListPage({
  store = sessionStore,
  queryLearnedWords = fetchLearnedWords,
}: {
  store?: SessionStore;
  queryLearnedWords?: () => Promise<LearnedWords | null>;
}) {
  const session = useSession(store);
  const readerId = readerIdOf(session);
  const log = useLogger();
  const [state, setState] = useState<WordsState>({ status: "loading" });

  useEffect(() => {
    if (readerId === null) return;
    let live = true;
    queryLearnedWords().then(
      (learned) => {
        if (!live) return;
        if (learned === null) {
          setState({ status: "failed" });
          return;
        }
        const { words, truncated } = learned;
        if (words.length > 0) {
          log.event("reading.word_list.load", {
            word_count: words.length,
            ...(truncated && { truncated }),
          });
        }
        setState({
          status: "ready",
          words: toWordListItems(words),
          truncated,
        });
      },
      (error: unknown) => {
        if (!live) return;
        log.error("reading.word_list.error", error);
        setState({ status: "failed" });
      },
    );
    return () => {
      live = false;
    };
  }, [readerId, queryLearnedWords, log]);

  if (session.status === "signed-out") {
    return (
      <div className={PAGE}>
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-6">
          <h2 className="mb-4 text-xl font-semibold">Sign In Required</h2>
          <p>Please sign in to view your word list</p>
          <Link
            to={saveProgressHref("/word-list")}
            className="mt-4 inline-block text-indigo-600 underline"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }
  // recoverUnavailableSession keeps retrying; a recovered session re-renders.
  if (session.status === "unavailable") {
    return (
      <div className={PAGE}>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
          <h2 className="mb-4 text-xl font-semibold">
            Can&apos;t Reach Your Word List
          </h2>
          <p>
            We can&apos;t connect right now. Your words will appear here once
            the connection is back.
          </p>
        </div>
      </div>
    );
  }
  if (readerId === null || state.status === "loading") {
    return (
      <div className={PAGE}>
        <Skeleton className="h-9 w-64" />
        <Skeleton className="mt-6 h-96 w-full" />
      </div>
    );
  }
  if (state.status === "failed") {
    return (
      <div className={PAGE}>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h2 className="mb-4 text-xl font-semibold">
            Unable to Load Word List
          </h2>
          <p>
            Something went wrong loading your words. Please try refreshing the
            page.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className={PAGE}>
      <h1 className="mb-6 text-3xl font-bold">Your Word List</h1>
      {state.words.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
          <p className="text-gray-600">
            No learned words yet. Start reading to build your word list!
          </p>
        </div>
      ) : (
        <SightVocabularyExercise words={state.words} />
      )}
      {state.truncated && (
        <p className="text-muted-foreground mt-6 text-xs">
          Showing the words you practised most recently; words you haven&apos;t
          practised in a while are not shown.
        </p>
      )}
    </div>
  );
}
